import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  CheckCircle, Sparkles, ArrowRight, ArrowLeft, ShoppingCart, Plus, Minus, Trash2,
  ChefHat, Star, Send, MessageCircle, Search, X, Info, Clock, Users,
  Heart, Cake, Utensils, Building2, Zap, MapPin, Home, HelpCircle,
  Package, Layers, FileText, Save
} from "lucide-react";

// Iter124u · P0 · Tipos de evento se cargan dinámicamente desde el tenant.
// El array constante queda como fallback neutro si el endpoint aún no responde.
const TIPOS_EVENTO_FALLBACK = ["Boda", "XV años", "Cumpleaños", "Bautizo", "Corporativo", "Otro"];

const PLACEHOLDER = (cat, tipo) => {
  const c = (cat || "").toLowerCase();
  const t = (tipo || "").toLowerCase();
  if (t.includes("experien") && c.includes("infantil")) return { grad: "from-amber-200 via-rose-200 to-pink-300", icon: Cake };
  if (t.includes("experien") && c.includes("premium")) return { grad: "from-amber-300 via-yellow-200 to-orange-300", icon: Star };
  if (t.includes("experien") && c.includes("catering")) return { grad: "from-orange-200 via-red-200 to-rose-300", icon: ChefHat };
  if (t.includes("experien") && c.includes("boutique")) return { grad: "from-fuchsia-200 via-pink-200 to-rose-300", icon: Sparkles };
  if (t.includes("experien")) return { grad: "from-rose-200 via-pink-200 to-fuchsia-300", icon: Heart };
  if (t.includes("menu") || c.includes("menu")) return { grad: "from-emerald-200 via-teal-200 to-cyan-300", icon: Utensils };
  if (t.includes("paquete") || t.includes("instalaciones")) return { grad: "from-indigo-200 via-purple-200 to-fuchsia-300", icon: Building2 };
  return { grad: "from-stone-200 via-amber-100 to-stone-300", icon: Zap };
};

const RANGOS_PRECIO = [
  { key: "all", label: "Todos", min: 0, max: Infinity },
  { key: "low", label: "≤ $500", min: 0, max: 500 },
  { key: "mid", label: "$500–$1,000", min: 500, max: 1000 },
  { key: "high", label: "$1,000–$3,000", min: 1000, max: 3000 },
  { key: "premium", label: "$3,000+", min: 3000, max: Infinity },
];

// Estado inicial completo (para reset)
const initialState = {
  paso: 1,
  cliente: { nombre: "", whatsapp: "", correo: "" },
  evento: {
    tipo: "", fecha: "", hora_inicio: "16:00", hora_fin: "23:00",
    adultos: 50, ninos: 0,
    ubicacion_tipo: "", // "petit" | "domicilio" | "sin_ubicacion"
    ubicacion_detalle: "",
    modalidad: "", // "petit_instalaciones" | "petit_paquete" | "dom_paquete" | "dom_servicios" | "personalizado"
    notas: "",
  },
  cart: [],
  descripcion_personalizada: "",
};

const CotizarPublico = () => {
  const { usuarioId } = useParams();
  const STORAGE_KEY = `celebra_borrador_${usuarioId}`;

  const [state, setState] = useState(initialState);
  const [empresa, setEmpresa] = useState({});
  // Iter125 · P0 · "Sede propia" (instalaciones fisicas) es exclusivo de Celebra Group.
  // El backend solo pone sede_propia_disponible=true para el tenant de Celebra; para
  // cualquier otro tenant (nuevo o existente) esta opcion no debe aparecer ni mostrarse,
  // para no filtrar la locacion de una empresa en el cotizador publico de otra.
  const sedePropiaDisponible = !!empresa.sede_propia_disponible;
  // Iter131 · P2 · Nombre de la sede propia se resuelve dinámicamente desde el
  // tenant. Si el tenant no configuró un nombre específico, usar una etiqueta
  // neutral. Nunca usar el nombre de Celebra como fallback para otros tenants.
  const SEDE_PROPIA_LABEL = empresa.sede_propia_nombre
    || empresa.nombre_comercial
    || empresa.razon_social
    || empresa.nombre_empresa
    || "Sede del proveedor";
  const [tiposEvento, setTiposEvento] = useState(TIPOS_EVENTO_FALLBACK);
  const [catalogo, setCatalogo] = useState({ items: [], servicios: [], menus: [] });
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [rangoPrecio, setRangoPrecio] = useState("all");
  const [tabCat, setTabCat] = useState("todo");
  const [itemDetalle, setItemDetalle] = useState(null);
  const [showBorradorPrompt, setShowBorradorPrompt] = useState(false);
  const [borradorInfo, setBorradorInfo] = useState(null);

  // ==== Auto-save en localStorage ====
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    // No guardamos hasta que termine la carga inicial
    if (loading) return;
    if (resultado) { try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ } return; }
    try {
      const snapshot = {
        state: stateRef.current,
        saved_at: new Date().toISOString(),
        cart_count: stateRef.current.cart.length,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch { /* noop */ }
  }, [state, loading, resultado, STORAGE_KEY]);

  const setEvento = (patch) => setState(s => ({ ...s, evento: { ...s.evento, ...patch } }));
  const setCliente = (patch) => setState(s => ({ ...s, cliente: { ...s.cliente, ...patch } }));
  const setPaso = (n) => setState(s => ({ ...s, paso: n }));
  const setCart = (updater) => setState(s => ({ ...s, cart: typeof updater === "function" ? updater(s.cart) : updater }));

  // ==== Fetch catálogo ====
  const fetchCatalogo = useCallback(async () => {
    setLoading(true);
    try {
      const [res, idnRes, tps] = await Promise.all([
        axios.get(`${API}/cotizar-publico/${usuarioId}/catalogo`),
        axios.get(`${API}/identidades-comerciales/publica/${usuarioId}`).catch(() => ({ data: null })),
        axios.get(`${API}/cotizar-publico/${usuarioId}/tipos-evento`).catch(() => ({ data: null })),
      ]);
      if (Array.isArray(tps.data) && tps.data.length > 0) setTiposEvento(tps.data);
      setCatalogo({
        items: res.data.items || [],
        servicios: res.data.servicios || [],
        menus: res.data.menus || [],
      });
      // Iter124r · P1 · Prioridad: identidad comercial pública > branding empresa
      const empBase = res.data.empresa || {};
      const idn = idnRes.data;
      setEmpresa(idn ? {
        ...empBase,
        nombre_empresa: idn.nombre_comercial || empBase.nombre_empresa,
        logo: idn.logo_url || empBase.logo,
        telefono: idn.telefono || empBase.telefono,
        correo: idn.correo || empBase.correo,
        direccion: idn.direccion || empBase.direccion,
        footer_texto: idn.footer_texto,
        color_primario: idn.color_primario,
        identidad_comercial_id: idn.identidad_id,
      } : empBase);
      // Detectar borrador previo
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.state && (parsed.state.cliente?.nombre || parsed.state.evento?.tipo || parsed.state.cart?.length)) {
            setBorradorInfo(parsed);
            setShowBorradorPrompt(true);
          }
        }
      } catch { /* noop */ }
    } catch { toast.error("Empresa no encontrada"); }
    finally { setLoading(false); }
  }, [usuarioId, STORAGE_KEY]);

  useEffect(() => { fetchCatalogo(); }, [fetchCatalogo]);

  const continuarBorrador = () => {
    if (borradorInfo?.state) setState(borradorInfo.state);
    setShowBorradorPrompt(false);
    toast.success("Continuando cotización guardada");
  };
  const empezarNueva = () => {
    setState(initialState);
    setShowBorradorPrompt(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    toast.info("Nueva cotización iniciada");
  };
  const descartarBorrador = () => {
    if (!window.confirm("¿Descartar la cotización guardada? Esta acción no se puede deshacer.")) return;
    setState(initialState);
    setShowBorradorPrompt(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    toast.info("Cotización descartada");
  };

  // ==== Derivados ====
  const { evento, cliente, cart, paso } = state;
  const adultos = parseInt(evento.adultos) || 0;
  const ninos = parseInt(evento.ninos) || 0;
  const totalInvitados = adultos + ninos;

  // Filtro de items por modalidad + ubicación
  const items = catalogo.items || [];
  const servicios = catalogo.servicios || [];

  // Todas las fuentes normalizadas
  const experiencias = useMemo(() => items.filter(i => (i.tipo || "").toLowerCase().includes("experien")), [items]);
  const paquetes = useMemo(() => items.filter(i => ["paquete", "instalaciones"].includes((i.tipo || "").toLowerCase())), [items]);
  const menusCat = useMemo(() => {
    const fromItems = items.filter(i => (i.tipo || "").toLowerCase() === "menu");
    const fromMenus = (catalogo.menus || [])
      .filter(m => m.nombre?.trim())
      .map(m => ({
        item_id: m.menu_id, nombre: m.nombre,
        descripcion: m.descripcion || `${(m.platillos || []).length} platillos incluidos`,
        precio: Number(m.precio_por_persona || 0),
        categoria: "menu", tipo: "menu",
        incluye: (m.platillos || []).map(p => p.nombre).filter(Boolean),
      }));
    const seen = new Set();
    return [...fromItems, ...fromMenus].filter(x => {
      const key = (x.nombre || "").toLowerCase().trim();
      if (!key || seen.has(key)) return false; seen.add(key); return true;
    });
  }, [items, catalogo.menus]);

  // Filtrar por ubicación si el item tiene el campo `ubicaciones_disponibles`
  const filtroUbicacion = (arr) => {
    const ub = evento.ubicacion_tipo;
    if (!ub || ub === "sin_ubicacion") return arr;
    return arr.filter(it => {
      const disp = it.ubicaciones_disponibles;
      if (!disp || !Array.isArray(disp) || disp.length === 0) return true; // sin restricción
      return disp.includes(ub);
    });
  };

  // Todos los items visibles según modalidad
  const itemsVisibles = useMemo(() => {
    const mod = evento.modalidad;
    if (mod === "petit_instalaciones") {
      // Paquetes tipo instalaciones (base) + servicios/extras
      const inst = filtroUbicacion(items.filter(i => (i.tipo || "").toLowerCase() === "instalaciones" || (i.categoria || "").toLowerCase().includes("instalacion")));
      return { paquete_base: inst, extras: [...servicios, ...menusCat] };
    }
    if (mod === "petit_paquete" || mod === "dom_paquete") {
      // Todo incluido — experiencias + paquetes
      return { paquete_base: filtroUbicacion([...experiencias, ...paquetes]), extras: [] };
    }
    if (mod === "dom_servicios") {
      // Servicios individuales — todo lo demás
      return { paquete_base: [], extras: [...menusCat, ...servicios, ...items.filter(i => !["experiencia", "instalaciones", "paquete"].includes((i.tipo || "").toLowerCase()))] };
    }
    return { paquete_base: [], extras: [] };
  }, [evento.modalidad, evento.ubicacion_tipo, experiencias, paquetes, menusCat, servicios, items]);

  // ==== Cart operations ====
  const calcSubtotal = (item) => {
    if (!item) return 0;
    const precio = parseFloat(item.precio) || 0;
    const qty = parseInt(item.qty) || 1;
    if (item.origen === "servicio_rapido") {
      const ct = item.cobro_tipo || "fijo";
      if (ct === "por_persona" && totalInvitados > 0) return precio * totalInvitados;
      if (ct === "por_nino" && ninos > 0) return precio * ninos;
      return precio * qty;
    }
    const t = (item.tipo || "").toLowerCase();
    if ((t.includes("paquete") || t.includes("men") || t.includes("experien")) && totalInvitados > 0) return precio * totalInvitados;
    return precio * qty;
  };
  const totalEstimado = cart.reduce((s, it) => s + calcSubtotal(it), 0);
  const iva = 0; // Se puede activar luego
  const anticipo = Math.round((totalEstimado + iva) * 0.5);

  const agregarAlCarrito = (item, origen = "catalogo") => {
    const id = item.item_id || item.catalogo_id || item.servicio_id;
    if (!id) { toast.error("Item sin ID"); return; }
    setCart(prev => {
      const existente = prev.find(c => c.item_id === id);
      if (existente) {
        toast.success(`+1 ${item.nombre}`);
        return prev.map(c => c.item_id === id ? { ...c, qty: (c.qty || 1) + 1 } : c);
      }
      toast.success(`Agregado: ${item.nombre}`);
      return [...prev, {
        key: `${origen}-${id}-${Date.now()}`,
        item_id: id, origen,
        tipo: item.tipo || (origen === "servicio_rapido" ? "extra" : "general"),
        nombre: item.nombre,
        precio: parseFloat(item.precio) || 0,
        qty: 1,
        cobro_tipo: item.cobro_tipo || "fijo",
        categoria: item.categoria || "",
        descripcion: item.descripcion || "",
        imagen_url: item.imagen_url || null,
      }];
    });
  };
  const cambiarQty = (key, delta) => setCart(prev => prev.map(c => c.key === key ? { ...c, qty: Math.max(1, (c.qty || 1) + delta) } : c));
  const quitarDelCarrito = (key) => setCart(prev => prev.filter(c => c.key !== key));
  const inCart = (id) => cart.some(c => c.item_id === id);

  // ==== Enviar ====
  const enviarSolicitud = async () => {
    if (!cliente.nombre?.trim() || !cliente.whatsapp?.trim()) { toast.error("Nombre y WhatsApp son requeridos"); return; }
    if (cart.length === 0 && evento.modalidad !== "personalizado") { toast.error("Tu carrito está vacío"); return; }
    setEnviando(true);
    try {
      const res = await axios.post(`${API}/cotizar-publico/${usuarioId}`, {
        cliente, evento: { ...evento, notas: [evento.notas, state.descripcion_personalizada].filter(Boolean).join(" · ") },
        cart_items: cart.map(c => ({ item_id: c.item_id, qty: c.qty || 1, origen: c.origen, tipo: c.tipo })),
        seleccion: {},
      });
      setResultado(res.data);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      setPaso(99);
      toast.success("¡Solicitud enviada!");
    } catch { toast.error("No se pudo enviar. Intenta de nuevo."); }
    finally { setEnviando(false); }
  };

  const abrirWhatsApp = () => {
    const tel = empresa.telefono?.replace(/\D/g, "") || "";
    const msg = encodeURIComponent(
      `Hola, soy ${cliente.nombre}. Envié una solicitud de cotización para ${evento.tipo} el ${evento.fecha}.\nTotal estimado: $${totalEstimado.toLocaleString("es-MX")}.\nReferencia: ${resultado?.cotizacion_id || ""}`
    );
    window.open(`https://wa.me/${tel}?text=${msg}`, "_blank");
  };

  // ==== Helpers de UI ====
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
      <div className="w-12 h-12 border-2 border-rose-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ==== RESULTADO ====
  if (paso === 99 && resultado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-emerald-600" />
          </div>
          <h1 className="font-display text-3xl text-stone-900 mb-2">¡Solicitud enviada!</h1>
          <p className="text-stone-600 mb-6">{resultado.mensaje || "Te contactaremos por WhatsApp muy pronto."}</p>
          <div className="bg-stone-50 rounded-2xl p-5 mb-6 text-left space-y-2">
            <div className="flex justify-between text-sm"><span className="text-stone-600">Total estimado:</span><span className="font-bold" data-testid="resultado-total">${(resultado.total_estimado || totalEstimado).toLocaleString("es-MX")}</span></div>
            <div className="flex justify-between text-sm"><span className="text-stone-600">Anticipo (50%):</span><span>${(resultado.anticipo_estimado || anticipo).toLocaleString("es-MX")}</span></div>
            <div className="flex justify-between text-xs pt-2 border-t border-stone-200"><span className="text-stone-500">Referencia:</span><code className="text-rose-700">{resultado.cotizacion_id?.slice(-8)}</code></div>
          </div>
          {empresa.telefono && (
            <Button onClick={abrirWhatsApp} className="w-full bg-emerald-600 hover:bg-emerald-700 mb-3" data-testid="btn-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" />Continuar por WhatsApp
            </Button>
          )}
          <Button onClick={() => { setState(initialState); setResultado(null); }} variant="outline" className="w-full">Hacer otra cotización</Button>
        </div>
      </div>
    );
  }

  // ==== HEADER ====
  const PASOS_LABELS = ["Datos", "Ubicación", "Modalidad", "Selección", "Resumen"];
  const renderHeader = () => (
    <div className="bg-white/95 backdrop-blur border-b border-stone-200 sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {empresa.logo && <img src={empresa.logo} alt="" className="w-9 h-9 rounded-xl object-cover shrink-0" />}
          <div className="min-w-0">
            <h1 className="font-display text-lg sm:text-2xl text-stone-900 truncate">{empresa.nombre_empresa || "Cotizador"}</h1>
            <p className="text-[10px] sm:text-xs text-stone-500 truncate">Un solo evento, un solo total. Te contactamos por WhatsApp.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {cart.length > 0 && (
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden relative" data-testid="btn-abrir-carrito-mobile">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="absolute -top-2 -right-2 bg-rose-600 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">{cart.length}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
                <SheetHeader className="sr-only"><SheetTitle>Tu cotización</SheetTitle></SheetHeader>
                {renderResumen(true)}
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
      {/* Stepper */}
      <div className="max-w-7xl mx-auto px-4 pb-3 flex items-center gap-2 text-xs overflow-x-auto">
        {PASOS_LABELS.map((label, i) => {
          const step = i + 1;
          const active = paso === step;
          const done = paso > step;
          return (
            <div key={label} className="flex items-center gap-2 shrink-0">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${active ? "bg-rose-700 text-white" : done ? "bg-emerald-600 text-white" : "bg-stone-200 text-stone-500"}`}>{done ? "✓" : step}</span>
              <span className={active ? "text-stone-900 font-medium" : "text-stone-500"}>{label}</span>
              {i < PASOS_LABELS.length - 1 && <span className="w-4 h-px bg-stone-300" />}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ==== RESUMEN CARRITO/COTIZACIÓN ====
  const renderResumen = (enDrawer = false) => (
    <div className={enDrawer ? "" : "sticky top-24 bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden"}>
      <div className={enDrawer ? "p-4 border-b border-stone-100 bg-gradient-to-br from-rose-50 to-amber-50" : "p-4 bg-gradient-to-br from-rose-50 to-amber-50 border-b border-stone-100"}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-lg flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-rose-700" />Tu cotización</h3>
          <span className="text-xs bg-rose-600 text-white px-2 py-0.5 rounded-full font-bold" data-testid="cart-count">{cart.length}</span>
        </div>
        {evento.tipo && <p className="text-xs text-stone-700">{evento.tipo} · {totalInvitados} invitados{evento.fecha ? ` · ${evento.fecha}` : ""}</p>}
        {evento.ubicacion_tipo === "petit" && sedePropiaDisponible && <p className="text-[11px] text-stone-500 mt-0.5">📍 {SEDE_PROPIA_LABEL}</p>}
        {evento.ubicacion_tipo === "domicilio" && evento.ubicacion_detalle && <p className="text-[11px] text-stone-500 mt-0.5 truncate">📍 {evento.ubicacion_detalle}</p>}
        {evento.ubicacion_tipo === "sin_ubicacion" && <p className="text-[11px] text-stone-500 mt-0.5">📍 Aún sin ubicación</p>}
      </div>
      <div className="p-4">
        {cart.length === 0 ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 mx-auto mb-2 rounded-full bg-stone-100 flex items-center justify-center"><ShoppingCart className="w-6 h-6 text-stone-400" /></div>
            <p className="text-sm text-stone-500">Sin conceptos aún.</p>
            <p className="text-xs text-stone-400 mt-1">Agrega paquetes, extras o servicios.</p>
          </div>
        ) : (
          <div className="space-y-2 mb-4 max-h-[45vh] overflow-y-auto pr-1">
            {cart.map(c => {
              const ph = PLACEHOLDER(c.categoria, c.tipo); const IconPh = ph.icon;
              return (
                <div key={c.key} className="border border-stone-200 rounded-xl p-2 flex gap-2 items-start" data-testid={`cart-item-${c.item_id}`}>
                  <div className={`w-11 h-11 rounded-lg overflow-hidden shrink-0 bg-gradient-to-br ${ph.grad} flex items-center justify-center`}>
                    {c.imagen_url ? <img src={c.imagen_url} alt="" className="w-full h-full object-cover" /> : <IconPh className="w-4 h-4 text-white/90" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <span className="text-sm font-medium text-stone-900 line-clamp-1 leading-tight">{c.nombre}</span>
                      <button onClick={() => quitarDelCarrito(c.key)} className="text-stone-400 hover:text-red-500 p-0.5" data-testid={`cart-del-${c.item_id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                    {c.descripcion && (
                      <p className="text-[11px] text-stone-500 line-clamp-2 leading-snug mt-0.5" data-testid={`cart-desc-${c.item_id}`}>{c.descripcion}</p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-1 bg-stone-100 rounded-full px-1 py-0.5">
                        <button onClick={() => cambiarQty(c.key, -1)} className="hover:bg-stone-200 rounded-full w-5 h-5 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <span className="text-xs w-5 text-center">{c.qty || 1}</span>
                        <button onClick={() => cambiarQty(c.key, 1)} className="hover:bg-stone-200 rounded-full w-5 h-5 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                      <span className="font-medium text-stone-900 text-sm">${calcSubtotal(c).toLocaleString("es-MX")}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="border-t border-stone-200 pt-3 space-y-1 text-sm">
          <div className="flex justify-between text-xs text-stone-600"><span>Invitados</span><span>{totalInvitados}</span></div>
          <div className="flex justify-between text-xs text-stone-600"><span>Subtotal</span><span>${totalEstimado.toLocaleString("es-MX")}</span></div>
          <div className="flex justify-between text-lg font-bold pt-1"><span className="text-stone-800">Total</span><span className="text-rose-700" data-testid="cart-total">${totalEstimado.toLocaleString("es-MX")}</span></div>
          <div className="flex justify-between text-xs text-emerald-700"><span>Anticipo (50%)</span><span>${anticipo.toLocaleString("es-MX")}</span></div>
          <p className="text-[10px] text-stone-500 pt-1">Preliminar. Se confirma al contactarte.</p>
        </div>
      </div>
    </div>
  );

  // ==== CARD DE ITEM ====
  const renderCard = (item, origen = "catalogo") => {
    const id = item.item_id || item.servicio_id;
    const isIn = inCart(id);
    const ph = PLACEHOLDER(item.categoria, item.tipo); const IconPh = ph.icon;
    const isServicio = origen === "servicio_rapido";
    const sufijo = isServicio
      ? (item.cobro_tipo === "por_persona" ? "/persona" : item.cobro_tipo === "por_nino" ? "/niño" : item.cobro_tipo === "por_hora" ? "/hora" : "fijo")
      : ((item.tipo === "experiencia" || item.tipo === "menu" || item.tipo === "paquete") ? "/persona" : "fijo");

    return (
      <div key={`${origen}-${id}`} className={`bg-white border rounded-2xl overflow-hidden flex flex-col transition-all hover:shadow-lg hover:-translate-y-0.5 ${isIn ? "border-rose-400 ring-1 ring-rose-200" : "border-stone-200"}`} data-testid={`cat-card-${id}`}>
        <button onClick={() => setItemDetalle({ item, origen })} className={`relative w-full h-40 sm:h-44 bg-gradient-to-br ${ph.grad} flex items-center justify-center overflow-hidden group`} data-testid={`card-image-${id}`}>
          {item.imagen_url ? <img src={item.imagen_url} alt={item.nombre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <IconPh className="w-14 h-14 text-white/70" strokeWidth={1.2} />}
          {item.categoria && <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-white/95 text-stone-700 px-2 py-0.5 rounded-full font-medium shadow-sm">{item.categoria}</span>}
          <span className="absolute bottom-2 right-2 text-[10px] uppercase tracking-wider bg-white/90 text-stone-500 px-2 py-0.5 rounded-full"><Info className="w-3 h-3 inline mr-0.5" />Ver detalles</span>
        </button>
        <div className="p-3 sm:p-4 flex flex-col flex-1">
          <h3 className="font-display text-base sm:text-lg text-stone-900 line-clamp-1 leading-tight">{item.nombre}</h3>
          {item.descripcion && <p className="text-xs text-stone-600 line-clamp-2 mt-1">{item.descripcion}</p>}
          <div className="flex items-end justify-between mt-3 pt-3 border-t border-stone-100">
            <div>
              <p className="text-rose-700 font-bold text-lg leading-none">${(item.precio || 0).toLocaleString("es-MX")}</p>
              <p className="text-[10px] text-stone-500 mt-0.5">{sufijo}</p>
            </div>
            <Button size="sm" onClick={(e) => { e.stopPropagation(); agregarAlCarrito(item, origen); }} className={isIn ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-700 hover:bg-rose-800 text-white"} data-testid={`btn-add-${id}`}>
              {isIn ? <><CheckCircle className="w-3.5 h-3.5 mr-1" />En cotización</> : <><Plus className="w-3.5 h-3.5 mr-1" />Agregar</>}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ==== PASO 1: DATOS INICIALES ====
  if (paso === 1) {
    const puedeAvanzar = cliente.nombre.trim() && cliente.whatsapp.trim() && evento.tipo && evento.fecha && adultos > 0;
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
        {renderHeader()}
        <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 sm:p-6">
            <h2 className="font-display text-2xl sm:text-3xl mb-1">Cuéntanos de tu evento</h2>
            <p className="text-sm text-stone-600 mb-5">Empecemos con lo básico para preparar tu cotización.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Nombre completo *</Label><Input value={cliente.nombre} onChange={(e) => setCliente({ nombre: e.target.value })} className="mt-1" data-testid="input-cliente-nombre" autoComplete="off" /></div>
              <div className="sm:col-span-2"><Label>WhatsApp *</Label><Input type="tel" inputMode="tel" value={cliente.whatsapp} onChange={(e) => setCliente({ whatsapp: e.target.value })} placeholder="10 digitos: 9991234567" className="mt-1" data-testid="input-cliente-whatsapp" autoComplete="off" /></div>
              <div className="sm:col-span-2">
                <Label>Tipo de evento *</Label>
                <select value={evento.tipo} onChange={(e) => setEvento({ tipo: e.target.value })} className="w-full mt-1 border border-stone-300 rounded-md px-3 py-2.5 text-base bg-white" data-testid="select-tipo-evento">
                  <option value="">Selecciona…</option>
                  {tiposEvento.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><Label>Fecha *</Label><Input type="date" value={evento.fecha} onChange={(e) => setEvento({ fecha: e.target.value })} className="mt-1" data-testid="input-fecha" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Hora inicio</Label><Input type="time" value={evento.hora_inicio} onChange={(e) => setEvento({ hora_inicio: e.target.value })} className="mt-1" data-testid="input-hi" /></div>
                <div><Label>Hora fin</Label><Input type="time" value={evento.hora_fin} onChange={(e) => setEvento({ hora_fin: e.target.value })} className="mt-1" data-testid="input-hf" /></div>
              </div>
              <div><Label>Adultos *</Label><Input type="number" min="1" value={evento.adultos} onChange={(e) => setEvento({ adultos: e.target.value })} className="mt-1" data-testid="input-adultos" /></div>
              <div><Label>Niños</Label><Input type="number" min="0" value={evento.ninos} onChange={(e) => setEvento({ ninos: e.target.value })} className="mt-1" data-testid="input-ninos" /></div>
            </div>
            <p className="text-[11px] text-stone-500 mt-4 flex items-center gap-1"><Save className="w-3 h-3" />Todo se guarda automáticamente. Puedes cerrar y regresar cuando quieras.</p>
          </div>
          <Button onClick={() => setPaso(2)} disabled={!puedeAvanzar} className="w-full bg-rose-700 hover:bg-rose-800 text-white py-6 shadow-md" data-testid="btn-continuar-ubicacion">
            Siguiente: ubicación <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          {!puedeAvanzar && <p className="text-xs text-center text-amber-700">Completa nombre, WhatsApp, tipo, fecha y adultos.</p>}
        </div>
        {renderBorradorPrompt()}
      </div>
    );
  }

  // ==== PASO 2: UBICACIÓN ====
  if (paso === 2) {
    const opciones = [
      ...(sedePropiaDisponible ? [{ key: "petit", icon: Building2, label: SEDE_PROPIA_LABEL, desc: "Nuestra locación con instalaciones, alberca y salón" }] : []),
      { key: "domicilio", icon: Home, label: "A domicilio u otra locación", desc: "Llevamos el servicio donde nos indiques" },
      { key: "sin_ubicacion", icon: HelpCircle, label: "Aún no tengo ubicación", desc: "Podemos ayudarte a decidir después" },
    ];
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
        {renderHeader()}
        <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 sm:p-6">
            <h2 className="font-display text-2xl sm:text-3xl mb-1">¿Dónde deseas realizar tu evento?</h2>
            <p className="text-sm text-stone-600 mb-5">Esto nos ayuda a mostrarte solo las opciones disponibles.</p>
            <div className="grid gap-3">
              {opciones.map(o => {
                const Icon = o.icon;
                const active = evento.ubicacion_tipo === o.key;
                return (
                  <button key={o.key} onClick={() => setEvento({ ubicacion_tipo: o.key, modalidad: "" })} className={`text-left p-4 rounded-2xl border-2 transition-all flex items-start gap-3 ${active ? "border-rose-600 bg-rose-50" : "border-stone-200 hover:border-stone-400 bg-white"}`} data-testid={`opt-ubicacion-${o.key}`}>
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-rose-600 text-white" : "bg-stone-100 text-stone-500"}`}><Icon className="w-5 h-5" /></div>
                    <div className="flex-1">
                      <p className={`font-medium ${active ? "text-rose-900" : "text-stone-900"}`}>{o.label}</p>
                      <p className="text-xs text-stone-600 mt-0.5">{o.desc}</p>
                    </div>
                    {active && <CheckCircle className="w-5 h-5 text-rose-600 shrink-0" />}
                  </button>
                );
              })}
            </div>
            {evento.ubicacion_tipo === "domicilio" && (
              <div className="mt-4">
                <Label>Ubicación del evento *</Label>
                <Input value={evento.ubicacion_detalle} onChange={(e) => setEvento({ ubicacion_detalle: e.target.value })} placeholder="Colonia, dirección o salón" className="mt-1" data-testid="input-ubicacion-detalle" autoComplete="off" />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setPaso(1)} variant="outline" className="flex-1" data-testid="btn-volver-1"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button>
            <Button
              onClick={() => {
                if (!evento.ubicacion_tipo) return toast.error("Elige una opción");
                if (evento.ubicacion_tipo === "domicilio" && !evento.ubicacion_detalle.trim()) return toast.error("Escribe la ubicación");
                setPaso(3);
              }}
              className="flex-1 bg-rose-700 hover:bg-rose-800 text-white py-6"
              data-testid="btn-continuar-modalidad"
            >
              Siguiente <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==== PASO 3: MODALIDAD ====
  if (paso === 3) {
    const modOpts = evento.ubicacion_tipo === "petit"
      ? [
          { key: "petit_instalaciones", icon: Building2, label: "Uso de instalaciones + extras", desc: "Rentas el espacio y armas tu evento con extras a elegir" },
          { key: "petit_paquete", icon: Sparkles, label: "Paquete todo incluido", desc: "Alimentos, bebidas, mobiliario, personal y decoración incluidos" },
          { key: "personalizado", icon: FileText, label: "Evento personalizado", desc: "Un planner te contactará para armar tu propuesta a medida" },
        ]
      : evento.ubicacion_tipo === "domicilio"
      ? [
          { key: "dom_paquete", icon: Sparkles, label: "Paquete todo incluido", desc: "Todo incluido en un solo precio (llevamos hasta tu ubicación)" },
          { key: "dom_servicios", icon: Layers, label: "Servicios individuales", desc: "Elige exactamente lo que necesitas: catering, decoración, mobiliario, etc." },
          { key: "personalizado", icon: FileText, label: "Evento personalizado", desc: "Cuéntanos tu idea y un planner arma tu propuesta a medida" },
        ]
      : [
          { key: "personalizado", icon: FileText, label: "Evento personalizado", desc: "Un planner te contactará para ayudarte a definir todo" },
        ];
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
        {renderHeader()}
        <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 sm:p-6">
            <h2 className="font-display text-2xl sm:text-3xl mb-1">¿Cómo prefieres organizar?</h2>
            <p className="text-sm text-stone-600 mb-5">Elige la modalidad que mejor se adapte a lo que buscas.</p>
            <div className="grid gap-3">
              {modOpts.map(m => {
                const Icon = m.icon;
                const active = evento.modalidad === m.key;
                return (
                  <button key={m.key} onClick={() => setEvento({ modalidad: m.key })} className={`text-left p-4 rounded-2xl border-2 transition-all flex items-start gap-3 ${active ? "border-rose-600 bg-rose-50" : "border-stone-200 hover:border-stone-400 bg-white"}`} data-testid={`opt-modalidad-${m.key}`}>
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-rose-600 text-white" : "bg-stone-100 text-stone-500"}`}><Icon className="w-5 h-5" /></div>
                    <div className="flex-1">
                      <p className={`font-medium ${active ? "text-rose-900" : "text-stone-900"}`}>{m.label}</p>
                      <p className="text-xs text-stone-600 mt-0.5">{m.desc}</p>
                    </div>
                    {active && <CheckCircle className="w-5 h-5 text-rose-600 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setPaso(2)} variant="outline" className="flex-1"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button>
            <Button onClick={() => { if (!evento.modalidad) return toast.error("Elige una modalidad"); setPaso(4); }} className="flex-1 bg-rose-700 hover:bg-rose-800 text-white py-6" data-testid="btn-continuar-seleccion">
              Siguiente <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==== PASO 4: SELECCIÓN ====
  if (paso === 4) {
    // Caso personalizado: solo textarea
    if (evento.modalidad === "personalizado") {
      return (
        <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
          {renderHeader()}
          <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-rose-700" />
                <h2 className="font-display text-2xl sm:text-3xl">Cuéntanos tu idea</h2>
              </div>
              <p className="text-sm text-stone-600 mb-5">Un planner revisará tu propuesta y te contactará por WhatsApp para armar un paquete a medida.</p>
              <Label>Describe tu evento ideal</Label>
              <Textarea
                value={state.descripcion_personalizada}
                onChange={(e) => setState(s => ({ ...s, descripcion_personalizada: e.target.value }))}
                placeholder="Por ejemplo: quiero una boda al aire libre con temática vintage, música en vivo, cena de 4 tiempos y área de fotos..."
                rows={6}
                className="mt-1"
                data-testid="input-descripcion-personalizada"
              />
              <p className="text-[11px] text-stone-500 mt-2">Mientras más detalles compartas, más precisa será tu propuesta.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setPaso(3)} variant="outline" className="flex-1"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button>
              <Button onClick={() => setPaso(5)} disabled={!state.descripcion_personalizada.trim()} className="flex-1 bg-rose-700 hover:bg-rose-800 text-white py-6" data-testid="btn-continuar-resumen">
                Ver resumen <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Caso normal: catálogo filtrado
    const { paquete_base, extras } = itemsVisibles;
    // Tabs: si petit_instalaciones → base+extras; si paquete → solo base; si dom_servicios → categorías de extras
    const showBase = paquete_base.length > 0 && (evento.modalidad === "petit_instalaciones" || evento.modalidad.endsWith("_paquete"));
    const showExtras = extras.length > 0 && (evento.modalidad === "petit_instalaciones" || evento.modalidad === "dom_servicios");

    const tabs = [
      showBase && { key: "base", label: evento.modalidad === "petit_instalaciones" ? "Instalaciones" : "Paquetes", items: paquete_base, origen: "catalogo" },
      showExtras && { key: "extras", label: "Extras y servicios", items: extras, origen: null },
    ].filter(Boolean);
    if (tabs.length === 0) tabs.push({ key: "todo", label: "Opciones", items: [...paquete_base, ...extras], origen: null });
    if (!tabs.find(t => t.key === tabCat)) setTabCat(tabs[0].key);
    const tabActiva = tabs.find(t => t.key === tabCat) || tabs[0];

    const rango = RANGOS_PRECIO.find(r => r.key === rangoPrecio);
    const q = busqueda.trim().toLowerCase();
    const filtered = tabActiva.items.filter(it => {
      const p = Number(it.precio) || 0;
      if (p < rango.min || p > rango.max) return false;
      if (!q) return true;
      return (it.nombre || "").toLowerCase().includes(q) || (it.descripcion || "").toLowerCase().includes(q);
    });

    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
        {renderHeader()}
        <div className="max-w-7xl mx-auto p-3 sm:p-6 grid lg:grid-cols-[1fr,360px] gap-4">
          <div>
            {tabs.length > 1 && (
              <div className="bg-white rounded-2xl border border-stone-200 mb-4 p-1.5 shadow-sm">
                <div className="grid grid-cols-2 gap-1">
                  {tabs.map(t => {
                    const active = tabCat === t.key;
                    return (
                      <button key={t.key} onClick={() => setTabCat(t.key)} className={`py-2.5 px-2 text-sm font-medium flex items-center justify-center gap-1.5 rounded-xl transition-all ${active ? "bg-rose-700 text-white shadow" : "text-stone-600 hover:bg-stone-50"}`} data-testid={`tab-${t.key}`}>
                        <Package className="w-4 h-4" />{t.label}
                        <span className={`text-[10px] px-1.5 rounded-full ${active ? "bg-white/25 text-white" : "bg-stone-200 text-stone-700"}`}>{t.items.length}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {tabActiva.items.length > 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-3 mb-4 shadow-sm space-y-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar..." className="w-full pl-9 pr-9 py-2 border border-stone-200 rounded-xl text-sm" data-testid="input-busqueda" />
                  {busqueda && <button onClick={() => setBusqueda("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400"><X className="w-4 h-4" /></button>}
                </div>
                <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
                  {RANGOS_PRECIO.map(r => (
                    <button key={r.key} onClick={() => setRangoPrecio(r.key)} className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap font-medium shrink-0 ${rangoPrecio === r.key ? "bg-rose-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`filtro-precio-${r.key}`}>{r.label}</button>
                  ))}
                </div>
              </div>
            )}
            {tabActiva.items.length === 0 ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-8 sm:p-10 text-center" data-testid="empty-sin-opciones">
                <Package className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p className="text-sm font-medium text-stone-700">No hay opciones disponibles para esta modalidad y ubicación.</p>
                <p className="text-xs mt-1 text-stone-500 mb-5">No pierdas tus datos: podemos armarte un evento a medida.</p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center max-w-md mx-auto">
                  <Button
                    onClick={() => { setEvento({ modalidad: "personalizado" }); toast.info("Cambiamos a evento personalizado, tus datos se conservan."); }}
                    className="flex-1 bg-rose-700 hover:bg-rose-800 text-white"
                    data-testid="btn-cambiar-personalizado"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Solicitar evento personalizado
                  </Button>
                  <Button onClick={() => setPaso(3)} variant="outline" className="flex-1" data-testid="btn-volver-modalidad-desde-empty">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Probar otra modalidad
                  </Button>
                </div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center text-stone-500">
                <Search className="w-10 h-10 mx-auto mb-3 text-stone-300" />
                <p className="text-sm">Nada coincide con tu búsqueda.</p>
                <button onClick={() => { setBusqueda(""); setRangoPrecio("all"); }} className="text-rose-700 text-xs font-medium mt-2">Limpiar filtros</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="grid-items">
                {filtered.map(it => {
                  const isServicio = it.servicio_id && !it.item_id;
                  return renderCard(it, isServicio ? "servicio_rapido" : "catalogo");
                })}
              </div>
            )}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => setPaso(3)} variant="outline" className="flex-1"><ArrowLeft className="w-4 h-4 mr-2" />Volver</Button>
              <Button onClick={() => setPaso(5)} disabled={cart.length === 0} className="flex-1 bg-rose-700 hover:bg-rose-800 text-white py-6 shadow-md" data-testid="btn-continuar-resumen">
                Ver resumen <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
          <div className="hidden lg:block">{renderResumen()}</div>
        </div>
        {cart.length > 0 && (
          <button onClick={() => setCartOpen(true)} className="lg:hidden fixed bottom-4 right-4 z-40 bg-rose-700 text-white rounded-full shadow-2xl px-5 py-3 flex items-center gap-2 font-medium" data-testid="fab-carrito">
            <ShoppingCart className="w-5 h-5" /><span>{cart.length} · ${totalEstimado.toLocaleString("es-MX")}</span>
          </button>
        )}
        <Dialog open={!!itemDetalle} onOpenChange={(v) => !v && setItemDetalle(null)}>
          <DialogContent className="max-w-lg p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="modal-detalle">
            {itemDetalle && (() => {
              const it = itemDetalle.item;
              const ph = PLACEHOLDER(it.categoria, it.tipo); const IconPh = ph.icon;
              const isIn = inCart(it.item_id || it.servicio_id);
              return (
                <>
                  <div className={`relative w-full h-56 bg-gradient-to-br ${ph.grad} flex items-center justify-center`}>
                    {it.imagen_url ? <img src={it.imagen_url} alt={it.nombre} className="w-full h-full object-cover" /> : <IconPh className="w-20 h-20 text-white/70" strokeWidth={1.2} />}
                    {it.categoria && <span className="absolute top-3 left-3 text-[11px] uppercase tracking-wider bg-white/95 text-stone-700 px-2.5 py-1 rounded-full font-medium">{it.categoria}</span>}
                  </div>
                  <div className="p-5 sm:p-6">
                    <DialogHeader className="mb-3"><DialogTitle className="font-display text-2xl">{it.nombre}</DialogTitle></DialogHeader>
                    {it.descripcion && <p className="text-sm text-stone-700">{it.descripcion}</p>}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {it.duracion_horas > 0 && <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-700 flex items-center gap-1"><Clock className="w-3 h-3" />{it.duracion_horas}h</span>}
                      {it.minimo_personas > 0 && <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-700 flex items-center gap-1"><Users className="w-3 h-3" />Mín. {it.minimo_personas}</span>}
                      {it.precio_nino > 0 && <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-700">Niño: ${it.precio_nino}</span>}
                    </div>
                    {Array.isArray(it.incluye) && it.incluye.length > 0 && (
                      <div className="mt-5">
                        <p className="text-[11px] uppercase tracking-wider text-stone-500 font-medium mb-2">Incluye</p>
                        <ul className="space-y-1.5">
                          {it.incluye.map((inc, i) => <li key={`d-${i}`} className="text-sm text-stone-700 flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>{inc}</span></li>)}
                        </ul>
                      </div>
                    )}
                    <div className="mt-6 pt-4 border-t border-stone-200 flex items-end justify-between gap-3">
                      <div>
                        <p className="text-3xl font-bold text-rose-700 leading-none">${(it.precio || 0).toLocaleString("es-MX")}</p>
                        <p className="text-xs text-stone-500 mt-1">{(it.tipo === "experiencia" || it.tipo === "menu" || it.tipo === "paquete") ? "por persona" : itemDetalle.origen === "servicio_rapido" && it.cobro_tipo === "por_persona" ? "por persona" : "precio fijo"}</p>
                      </div>
                      <Button onClick={() => { agregarAlCarrito(it, itemDetalle.origen); setItemDetalle(null); }} className={isIn ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-700 hover:bg-rose-800 text-white"} data-testid="btn-agregar-desde-detalle">
                        {isIn ? <><CheckCircle className="w-4 h-4 mr-1" />En cotización · +1</> : <><Plus className="w-4 h-4 mr-1" />Agregar</>}
                      </Button>
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ==== PASO 5: RESUMEN Y ENVIAR ====
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50">
      {renderHeader()}
      <div className="max-w-4xl mx-auto p-4 sm:p-6 grid lg:grid-cols-[1fr,320px] gap-4">
        <div className="space-y-4">
          {/* Datos del evento */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
            <h2 className="font-display text-2xl mb-4">Confirma tu cotización</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoRow label="Cliente" value={cliente.nombre} />
              <InfoRow label="WhatsApp" value={cliente.whatsapp} />
              <InfoRow label="Tipo" value={evento.tipo} />
              <InfoRow label="Fecha" value={evento.fecha} />
              <InfoRow label="Horario" value={`${evento.hora_inicio} - ${evento.hora_fin}`} />
              <InfoRow label="Adultos" value={adultos} />
              <InfoRow label="Niños" value={ninos} />
              <InfoRow label="Ubicación" value={evento.ubicacion_tipo === "petit" && sedePropiaDisponible ? SEDE_PROPIA_LABEL : evento.ubicacion_detalle || "Sin definir"} />
            </div>
            <button onClick={() => setPaso(1)} className="text-xs text-rose-700 hover:underline mt-3">Editar datos</button>
          </div>

          {/* Correo opcional */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
            <Label>Correo (opcional)</Label>
            <Input type="email" value={cliente.correo} onChange={(e) => setCliente({ correo: e.target.value })} placeholder="tucorreo@ejemplo.com" className="mt-1" data-testid="input-cliente-correo" />
          </div>

          {/* Conceptos */}
          {cart.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-xl">Conceptos ({cart.length})</h3>
                <button onClick={() => setPaso(4)} className="text-xs text-rose-700 hover:underline">Editar</button>
              </div>
              <div className="space-y-2">
                {cart.map(c => (
                  <div key={c.key} className="flex items-start justify-between border-b border-stone-100 pb-2 text-sm">
                    <div className="flex-1 min-w-0 pr-3">
                      <p className="font-medium">{c.nombre}</p>
                      {c.descripcion && (
                        <p className="text-[11px] text-stone-500 line-clamp-2 mt-0.5" data-testid={`resumen-desc-${c.item_id}`}>{c.descripcion}</p>
                      )}
                      <p className="text-xs text-stone-500 mt-0.5">${c.precio.toLocaleString("es-MX")} × {c.qty || 1}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-medium">${calcSubtotal(c).toLocaleString("es-MX")}</span>
                      <button onClick={() => quitarDelCarrito(c.key)} className="text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {evento.modalidad === "personalizado" && (
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
              <h3 className="font-display text-xl mb-2">Tu idea</h3>
              <p className="text-sm text-stone-700 whitespace-pre-wrap">{state.descripcion_personalizada}</p>
              <button onClick={() => setPaso(4)} className="text-xs text-rose-700 hover:underline mt-2">Editar</button>
            </div>
          )}

          {/* Totales */}
          {cart.length > 0 && (
            <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-2xl border border-rose-200 p-5 space-y-1.5">
              <div className="flex justify-between text-sm"><span className="text-stone-700">Subtotal</span><span>${totalEstimado.toLocaleString("es-MX")}</span></div>
              <div className="flex justify-between text-lg font-bold pt-1"><span>Total estimado</span><span className="text-rose-700">${totalEstimado.toLocaleString("es-MX")}</span></div>
              <div className="flex justify-between text-sm text-emerald-700"><span>Anticipo (50%)</span><span>${anticipo.toLocaleString("es-MX")}</span></div>
              <p className="text-[11px] text-stone-500 pt-1">Preliminar. Se ajusta al confirmar por WhatsApp.</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={() => setPaso(4)} variant="outline" className="flex-1"><ArrowLeft className="w-4 h-4 mr-2" />Editar conceptos</Button>
            <Button onClick={enviarSolicitud} disabled={enviando} className="flex-1 bg-rose-700 hover:bg-rose-800 text-white py-6 shadow-md" data-testid="btn-enviar-solicitud">
              {enviando ? "Enviando..." : <><Send className="w-4 h-4 mr-2" />Solicitar cotización</>}
            </Button>
          </div>
        </div>
        <div className="hidden lg:block">{renderResumen()}</div>
      </div>
    </div>
  );

  // ==== BORRADOR PROMPT ====
  function renderBorradorPrompt() {
    return (
      <Dialog open={showBorradorPrompt} onOpenChange={setShowBorradorPrompt}>
        <DialogContent className="max-w-md" data-testid="modal-borrador">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Save className="w-5 h-5 text-emerald-600" />Tienes una cotización en proceso</DialogTitle>
          </DialogHeader>
          {borradorInfo?.state && (
            <div className="text-sm space-y-1 py-2">
              {borradorInfo.state.cliente?.nombre && <p><span className="text-stone-500">Cliente:</span> <strong>{borradorInfo.state.cliente.nombre}</strong></p>}
              {borradorInfo.state.evento?.tipo && <p><span className="text-stone-500">Evento:</span> {borradorInfo.state.evento.tipo}</p>}
              {borradorInfo.state.evento?.fecha && <p><span className="text-stone-500">Fecha:</span> {borradorInfo.state.evento.fecha}</p>}
              {borradorInfo.state.cart?.length > 0 && <p><span className="text-stone-500">Conceptos:</span> {borradorInfo.state.cart.length}</p>}
              {borradorInfo.saved_at && <p className="text-xs text-stone-400 pt-1">Guardado: {new Date(borradorInfo.saved_at).toLocaleString("es-MX")}</p>}
            </div>
          )}
          <div className="flex flex-col gap-2 pt-3">
            <Button onClick={continuarBorrador} className="w-full bg-rose-700 hover:bg-rose-800 text-white" data-testid="btn-continuar-borrador">Continuar cotización</Button>
            <Button onClick={empezarNueva} variant="outline" className="w-full" data-testid="btn-empezar-nueva">Empezar una nueva</Button>
            <button onClick={descartarBorrador} className="text-xs text-red-600 hover:underline mt-1" data-testid="btn-descartar-borrador">Descartar cotización</button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
};

// Helper components
const InfoRow = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p>
    <p className="font-medium text-stone-800 truncate">{value || "—"}</p>
  </div>
);

export default CotizarPublico;
